/**
 * Type-safe route builder system
 *
 * Provides compile-time type checking for route parameters.
 * Usage:
 *   const route = defineRoute('/dashboard/:projectId/jobs/:jobId?');
 *   route.build({ projectId: 'abc' });           // '/dashboard/abc/jobs'
 *   route.build({ projectId: 'abc', jobId: '1' }); // '/dashboard/abc/jobs/1'
 */
/**
 * API prefix for backend routes.
 * Frontend apiClient adds this prefix automatically.
 * Backend should use withApiPrefix() to add this to route patterns.
 */
export const API_PREFIX = '/api';
/**
 * Prepends the API prefix to a route pattern.
 * Used by backend to ensure routes match what frontend expects.
 *
 * @example
 * withApiPrefix('/organizations') // => '/api/organizations'
 * withApiPrefix(apiRoutes.organizations.pattern) // => '/api/organizations'
 */
export function withApiPrefix(pattern) {
    return `${API_PREFIX}${pattern}`;
}
/**
 * Creates a type-safe route builder.
 *
 * @param pattern Route pattern with :param placeholders (e.g., '/dashboard/:projectId/jobs')
 * @returns Route object with pattern and build function
 *
 * @example
 * const route = defineRoute('/dashboard/:projectId/jobs/:jobId?');
 * route.pattern; // '/dashboard/:projectId/jobs/:jobId?'
 * route.build({ projectId: 'abc' }); // '/dashboard/abc/jobs'
 * route.build({ projectId: 'abc', jobId: '123' }); // '/dashboard/abc/jobs/123'
 */
export function defineRoute(pattern) {
    const build = (params) => {
        if (!params)
            return pattern;
        let result = pattern;
        // Replace all :param and :param? placeholders
        for (const [key, value] of Object.entries(params)) {
            if (value !== undefined) {
                // Replace both :param and :param? variants
                result = result.replace(`:${key}?`, value);
                result = result.replace(`:${key}`, value);
            }
        }
        // Remove any remaining optional params that weren't provided
        result = result.replace(/\/:[^/]+\?/g, '');
        // Clean up any double slashes that might result from removal
        result = result.replace(/\/+/g, '/');
        // Remove trailing slash unless it's the root
        if (result.length > 1 && result.endsWith('/')) {
            result = result.slice(0, -1);
        }
        return result;
    };
    return {
        pattern,
        build: build,
    };
}
//# sourceMappingURL=route-builder.js.map